import constants from '../../../../appConstants';
import iconRenderer from '../../../common/iconRenderer';
import {
  GeoCodeRenderer,
  SpecialNeedsValueGetter,
  SpecialNeedsRenderer
} from '@dataGrid/components/cellRenderer';
import {
  MultiSelectFilter,
  DateTimeFilter,
  StaticFilter
} from '@dataGrid/components/columnFilters';
import { formatDateTime, mergeUserConfig } from '@dataGrid/gridUtilities';
import { formatDayWindow, getDefaultHours, renderWindow } from '../EditCustomer/utils';
import { getSpecialNeedsData, formatBooleanYesNo } from '../utils';
import { formatPhone } from '../../Dispatch/utils/utils';

class GridConfig {
  constructor(configuration) {
    const config = configuration || {};
    this.name = 'GridConfiguration';
    this.config = GridConfig.getConfiguration(config);
  }
  static getConfiguration({ userConfig, featureFlags }) {
    const defaultConfig = GridConfig.getDefaultConfiguration(featureFlags);
    return mergeUserConfig({
      defaultConfig,
      userConfig
    });
  }

  static getLiftGate = (params) => {
    return params.data.pickupDetails.isLiftGateRequired ? 'L' : '';
  };

  static getTrailerSwapValue = (params) => {
    return params.data.pickupDetails.isTrailerSwap ? 'Yes' : '';
  };

  static defaultHoursValueGetter = (params) => {
    const {
      openHours = '',
      closeHours = '',
      openHours2 = '',
      closeHours2 = ''
    } = params.data['pickupDetails'] ?? {};
    return getDefaultHours({ openHours, closeHours, openHours2, closeHours2 });
  };
  static getSpecialNeedsOption(params) {
    return params.context.specialNeedsOptions;
  }

  static specialNeedsValueGetter(params, featureFlags) {
    const specialNeedsData = getSpecialNeedsData({
      data: params.data?.pickupDetails,
      isDelivery: false,
      featureFlags
    });
    return SpecialNeedsValueGetter({
      data: specialNeedsData
    });
  }

  static getEquipmentSizeColumns(featureFlags) {
    if (!featureFlags.enableEquipmentSizeForShipper) {
      return [];
    }

    const columns = [
      {
        headerName: 'Min Equip Size',
        headerTooltip: 'Min Equipment Size',
        field: 'pickupDetails.minEquipmentSize',
        minWidth: constants.columnWidth.W_140,
        cellClass: 'gridCell-leftAlign',
        filter: MultiSelectFilter
      },
      {
        headerName: 'Max Equip Size',
        headerTooltip: 'Max Equipment Size',
        field: 'pickupDetails.maxEquipmentSize',
        minWidth: constants.columnWidth.W_140,
        cellClass: 'gridCell-leftAlign',
        filter: MultiSelectFilter
      }
    ];

    if (featureFlags.enableFixedEquipmentSize) {
      columns.push({
        headerName: 'Fixed Equip Size',
        headerTooltip: 'Fixed Equipment Size',
        field: 'pickupDetails.fixedEquipmentSize',
        minWidth: constants.columnWidth.FIXED_TRAILER,
        cellClass: 'gridCell-leftAlign',
        filter: MultiSelectFilter
      });
    }

    return columns;
  }

  static getShipperFullAddress(params) {
    return [params.data.address, params.data.address2].filter(Boolean).join(', ');
  }

  static formatPhoneNumber = (params) => formatPhone(params.value);

  static getDefaultConfiguration(featureFlags) {
    return {
      title: constants.gridNames.ShipperGrid,
      keyColumn: 'code',
      rowSelection: 'multiple',
      rowDeselection: 'true',
      defaultColDef: {
        lockPinned: true,
        cellDataType: false
      },
      columns: [
        {
          headerName: 'Acc #',
          headerTooltip: 'Shipper Account Number',
          field: 'code',
          minWidth: constants.columnWidth.CUSTOMER_CODE,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          tooltipField: 'code',
          lockPosition: true
        },
        {
          headerName: 'Shipper Name',
          headerTooltip: 'Shipper Name',
          field: 'name',
          minWidth: constants.columnWidth.W_180,
          lockPosition: true,
          cellClass: ['gridCell-leftAlign', 'actionable-cell'],
          filter: MultiSelectFilter,
          tooltipField: 'name',
          filterParams: {
            width: constants.columnWidth.W_300
          }
        },
        {
          headerName: 'Spl Needs',
          headerTooltip: 'Special Needs',
          minWidth: constants.columnWidth.SPL_NEED,
          cellClass: 'gridCell-leftAlign',
          valueGetter: (params) => GridConfig.specialNeedsValueGetter(params, featureFlags),
          filter: StaticFilter,
          filterParams: {
            getStaticOptions: GridConfig.getSpecialNeedsOption
          },
          cellRenderer: SpecialNeedsRenderer
        },
        {
          headerName: 'Address',
          headerTooltip: 'Shipper Address',
          valueGetter: GridConfig.getShipperFullAddress,
          minWidth: constants.columnWidth.CUSTOMER_ADDRESS,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          tooltipValueGetter: (params) => params.value,
          filterParams: {
            width: constants.columnWidth.W_300
          }
        },
        {
          headerName: 'City',
          headerTooltip: 'City',
          field: 'city',
          cellClass: 'gridCell-leftAlign',
          minWidth: constants.columnWidth.CITY,
          filter: MultiSelectFilter,
          tooltipField: 'city'
        },
        {
          headerName: 'State',
          headerTooltip: 'State',
          field: 'state',
          cellClass: 'gridCell-leftAlign',
          minWidth: constants.columnWidth.STATE,
          filter: MultiSelectFilter
        },
        {
          headerName: 'Zip',
          headerTooltip: 'Zip',
          field: 'zip',
          cellClass: 'gridCell-leftAlign',
          minWidth: constants.columnWidth.ZIP_CODE,
          filter: MultiSelectFilter
        },
        {
          headerName: 'Liftgate',
          headerTooltip: 'Liftgate',
          valueGetter: GridConfig.getLiftGate,
          minWidth: constants.columnWidth.ROUTES,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter
        },
        {
          headerName: 'Trailer Swap',
          headerTooltip: 'Trailer Swap',
          field: 'pickupDetails.isTrailerSwap',
          valueGetter: GridConfig.getTrailerSwapValue,
          minWidth: constants.columnWidth.W_115,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter
        },
        {
          headerName: 'Geocoding',
          headerTooltip: 'Geocoding',
          field: 'pickupDetails.isGeoCoded',
          minWidth: constants.columnWidth.GEOCODING,
          cellClass: 'gridCell-left,Align',
          editable: false,
          cellRenderer: GeoCodeRenderer,
          filter: StaticFilter,
          filterParams: {
            staticOptions: Object.values(constants.geoCode),
            renderIcon: iconRenderer
          }
        },
        ...GridConfig.getEquipmentSizeColumns(featureFlags),
        {
          headerName: 'Default Hours',
          headerTooltip: 'Default Hours',
          valueGetter: GridConfig.defaultHoursValueGetter,
          minWidth: constants.columnWidth.W_140,
          cellClass: 'gridCell-leftAlign',
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue: true
          }
        },
        {
          headerName: 'Monday',
          headerTooltip: 'Monday',
          field: 'pickupDetails.shippingHours.monday',
          tooltipField: 'pickupDetails.shippingHours.monday',
          valueFormatter: formatDayWindow,
          minWidth: constants.columnWidth.W_140,
          cellClass: 'gridCell-leftAlign',
          cellRenderer: renderWindow,
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue: true
          }
        },
        {
          headerName: 'Tuesday',
          headerTooltip: 'Tuesday',
          field: 'pickupDetails.shippingHours.tuesday',
          tooltipField: 'pickupDetails.shippingHours.tuesday',
          valueFormatter: formatDayWindow,
          minWidth: constants.columnWidth.W_140,
          cellClass: 'gridCell-leftAlign',
          cellRenderer: renderWindow,
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue: true
          }
        },
        {
          headerName: 'Wednesday',
          headerTooltip: 'Wednesday',
          field: 'pickupDetails.shippingHours.wednesday',
          tooltipField: 'pickupDetails.shippingHours.wednesday',
          valueFormatter: formatDayWindow,
          minWidth: constants.columnWidth.W_140,
          cellClass: 'gridCell-leftAlign',
          cellRenderer: renderWindow,
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue: true
          }
        },
        {
          headerName: 'Thursday',
          headerTooltip: 'Thursday',
          field: 'pickupDetails.shippingHours.thursday',
          tooltipField: 'pickupDetails.shippingHours.thursday',
          valueFormatter: formatDayWindow,
          minWidth: constants.columnWidth.W_140,
          cellClass: 'gridCell-leftAlign',
          cellRenderer: renderWindow,
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue: true
          }
        },
        {
          headerName: 'Friday',
          headerTooltip: 'Friday',
          field: 'pickupDetails.shippingHours.friday',
          tooltipField: 'pickupDetails.shippingHours.friday',
          valueFormatter: formatDayWindow,
          minWidth: constants.columnWidth.W_140,
          cellClass: 'gridCell-leftAlign',
          cellRenderer: renderWindow,
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue: true
          }
        },
        {
          headerName: 'Saturday',
          headerTooltip: 'Saturday',
          field: 'pickupDetails.shippingHours.saturday',
          tooltipField: 'pickupDetails.shippingHours.saturday',
          valueFormatter: formatDayWindow,
          minWidth: constants.columnWidth.W_140,
          cellClass: 'gridCell-leftAlign',
          cellRenderer: renderWindow,
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue: true
          }
        },
        {
          headerName: 'Sunday',
          headerTooltip: 'Sunday',
          field: 'pickupDetails.shippingHours.sunday',
          tooltipField: 'pickupDetails.shippingHours.sunday',
          valueFormatter: formatDayWindow,
          minWidth: constants.columnWidth.W_140,
          cellClass: 'gridCell-leftAlign',
          cellRenderer: renderWindow,
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue: true
          }
        },
        {
          headerName: 'Stop Time',
          headerTooltip: 'Stop Time',
          field: 'pickupDetails.stopTime',
          minWidth: constants.columnWidth.W_110,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter
        },
        {
          headerName: 'Contact Name',
          headerTooltip: 'Contact Name',
          field: 'pickupDetails.shipperContactName',
          minWidth: constants.columnWidth.W_180,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          hide: true,
        },
        {
          headerName: 'Phone Number',
          headerTooltip: 'Phone Number',
          field: 'pickupDetails.phoneNumber',
          minWidth: constants.columnWidth.W_140,
          cellClass: 'gridCell-leftAlign',
          valueFormatter: GridConfig.formatPhoneNumber,
          filter: MultiSelectFilter,
          tooltipValueGetter: (params) => params.value,
          hide: true,
        },
        {
          headerName: 'Unattended Delivery',
          headerTooltip: 'Unattended Delivery',
          field: 'pickupDetails.isUnattendedDelivery',
          minWidth: constants.columnWidth.W_160,
          cellClass: 'gridCell-leftAlign',
          valueFormatter: formatBooleanYesNo,
          filter: StaticFilter,
          filterParams: {
            staticOptions: Object.values(constants.BooleanFilter)
          },
          isInvisibleInShowHide: !featureFlags.enableUnattendedDeliveryInCustomerManagement,
          shouldForceHideColumn: !featureFlags.enableUnattendedDeliveryInCustomerManagement,
        },
        {
          headerName: 'Delivery Proof Required',
          headerTooltip: 'Delivery Proof Required',
          field: 'pickupDetails.isDeliveryProofRequired',
          minWidth: constants.columnWidth.W_160,
          cellClass: 'gridCell-leftAlign',
          valueFormatter: formatBooleanYesNo,
          filter: StaticFilter,
          filterParams: {
            staticOptions: Object.values(constants.BooleanFilter)
          },
          isInvisibleInShowHide: !featureFlags.enableProofOfDeliveryInCustomerManagement,
          shouldForceHideColumn: !featureFlags.enableProofOfDeliveryInCustomerManagement,
        },
        {
          headerName: 'Requires Paper DR',
          headerTooltip: 'Requires Paper DR',
          field: 'pickupDetails.isPhysicalDrRequired',
          minWidth: constants.columnWidth.W_160,
          cellClass: 'gridCell-leftAlign',
          valueFormatter: formatBooleanYesNo,
          filter: StaticFilter,
          filterParams: {
            staticOptions: Object.values(constants.BooleanFilter)
          },
          isInvisibleInShowHide: !featureFlags.enableDRPreferenceInShipperManagement,
          shouldForceHideColumn: !featureFlags.enableDRPreferenceInShipperManagement,
        },
        {
          headerName: 'Comments',
          headerTooltip: 'Comments',
          field: 'pickupDetails.shipperComments',
          minWidth: constants.columnWidth.W_180,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          tooltipField: 'pickupDetails.shipperComments',
          hide: true,
          isInvisibleInShowHide: !featureFlags.enableCommentsForCustomer,
          shouldForceHideColumn: !featureFlags.enableCommentsForCustomer,
        },
        {
          headerName: 'Last Modified On',
          headerTooltip: 'Last Modified On',
          field: 'pickupDetails.lastModified.dateTime',
          valueFormatter: formatDateTime,
          minWidth: constants.columnWidth.W_120,
          cellClass: 'gridCell-leftAlign',
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.DATETIME
          }
        },
        {
          headerName: 'Last Modified By',
          headerTooltip: 'Last Modified By',
          field: 'pickupDetails.lastModified.userName',
          minWidth: constants.columnWidth.W_180,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          tooltipValueGetter: (params) => params.value
        }
      ]
    };
  }
}

export default GridConfig;
